/**
 * Short, human-friendly relative time formatter.
 *
 * Given a past Date, returns strings like "just now", "3m ago", "2h ago",
 * "5d ago", "3w ago", "4mo ago", "2y ago". Keeps each value to a single
 * compact unit — suited to dense UI like comment lists and activity feeds
 * where long strings ("3 hours, 25 minutes ago") would wrap and compete
 * with the comment body for space.
 *
 * Future dates (clock skew, optimistic writes) clamp to "just now" rather
 * than reporting nonsense like "-2s ago".
 */
const SECOND = 1000;
const MINUTE = 60 * SECOND;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;
const WEEK = 7 * DAY;
const MONTH = 30 * DAY; // Approximate; good enough for UI labels.
const YEAR = 365 * DAY;

export function formatRelativeTime(date: Date | null | undefined, now: Date = new Date()): string {
  if (!date || date.getTime() === 0) return '';
  const diffMs = now.getTime() - date.getTime();
  if (diffMs < 30 * SECOND) return 'just now';
  if (diffMs < MINUTE) return `${Math.floor(diffMs / SECOND)}s ago`;
  if (diffMs < HOUR) return `${Math.floor(diffMs / MINUTE)}m ago`;
  if (diffMs < DAY) return `${Math.floor(diffMs / HOUR)}h ago`;
  if (diffMs < WEEK) return `${Math.floor(diffMs / DAY)}d ago`;
  if (diffMs < MONTH) return `${Math.floor(diffMs / WEEK)}w ago`;
  if (diffMs < YEAR) return `${Math.floor(diffMs / MONTH)}mo ago`;
  return `${Math.floor(diffMs / YEAR)}y ago`;
}
